import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Field, Modal } from "./AVGS";
import { formatEUR } from "../lib/format";
import { Key, Users, Plus, Trash } from "@phosphor-icons/react";
import { PaymentAuditSection } from "../components/PaymentAuditSection";

const defaults = {
  price_per_ue: 57.87,
  payment_term_days_default: 20,
  revenue_target_monthly: 28136.33,
  break_even_monthly: 14052.80,
  profit_target_yearly: 168640.0,
  profit_target_monthly: 14053.33,
  default_personnel_cost_per_ue: 0.0,
  default_material_cost_flat: 0.0,
  default_room_cost_flat: 0.0,
  cashflow_green_threshold: 5000.0,
  cashflow_yellow_threshold: 0.0,
  cashflow_orange_threshold: -3000.0,
  starting_balance: 0.0,
  avgs_measure_types: [
    { name: "Einführung in den Arbeitsmarkt", price_per_ue: 61.51, price_history: [
      { label: "bis 31.07.2026", effective_from: null, price_per_ue: 57.87 },
      { label: "ab 01.08.2026", effective_from: "2026-08-01", price_per_ue: 61.51 },
    ] },
    { name: "Gründung", price_per_ue: 77.70, price_history: [] },
  ],
};

export default function SettingsPage() {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/settings").then(({ data }) => {
      if (!active) return;
      setForm((f) => ({ ...f, ...data }));
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        price_per_ue: parseFloat(form.price_per_ue),
        payment_term_days_default: parseInt(form.payment_term_days_default),
        revenue_target_monthly: parseFloat(form.revenue_target_monthly),
        break_even_monthly: parseFloat(form.break_even_monthly),
        profit_target_yearly: parseFloat(form.profit_target_yearly),
        profit_target_monthly: parseFloat(form.profit_target_monthly),
        default_personnel_cost_per_ue: parseFloat(form.default_personnel_cost_per_ue || 0),
        default_material_cost_flat: parseFloat(form.default_material_cost_flat || 0),
        default_room_cost_flat: parseFloat(form.default_room_cost_flat || 0),
        cashflow_green_threshold: parseFloat(form.cashflow_green_threshold ?? 5000),
        cashflow_yellow_threshold: parseFloat(form.cashflow_yellow_threshold ?? 0),
        cashflow_orange_threshold: parseFloat(form.cashflow_orange_threshold ?? -3000),
        starting_balance: parseFloat(form.starting_balance ?? 0),
        avgs_measure_types: (form.avgs_measure_types || []).filter(t => t.name && t.price_per_ue).map(t => ({
          name: String(t.name).trim(),
          price_per_ue: parseFloat(t.price_per_ue),
          price_history: (t.price_history || []).filter(p => p && p.price_per_ue).map(p => ({
            label: (p.label || "").trim(),
            effective_from: p.effective_from || null,
            price_per_ue: parseFloat(p.price_per_ue),
          })),
        })),
      };
      await api.put("/settings", payload);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[#5B6371]">Lade …</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <div className="label-eyebrow">Einstellungen</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1.5">Finanzielle Ziele</h1>
        <p className="text-sm text-[#5B6371] mt-1">Steuern Sie Break-even, Umsatzziel und Gewinnerwartung für Ihr Dashboard.</p>
      </header>

      <form onSubmit={onSave} className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5" data-testid="settings-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Preis pro UE (€)">
            <input type="number" step="0.01" className="field" value={form.price_per_ue}
              onChange={(e) => setForm({ ...form, price_per_ue: e.target.value })}
              data-testid="settings-price-per-ue" />
          </Field>
          <Field label="Standard Zahlungsziel (Tage)">
            <input type="number" className="field" value={form.payment_term_days_default}
              onChange={(e) => setForm({ ...form, payment_term_days_default: e.target.value })}
              data-testid="settings-payment-term" />
          </Field>
          <Field label="Umsatzziel / Monat (€)">
            <input type="number" step="0.01" className="field" value={form.revenue_target_monthly}
              onChange={(e) => setForm({ ...form, revenue_target_monthly: e.target.value })}
              data-testid="settings-revenue-target" />
          </Field>
          <Field label="Break-even / Monat (€)">
            <input type="number" step="0.01" className="field" value={form.break_even_monthly}
              onChange={(e) => setForm({ ...form, break_even_monthly: e.target.value })}
              data-testid="settings-break-even" />
          </Field>
          <Field label="Gewinnziel / Jahr (€)">
            <input type="number" step="0.01" className="field" value={form.profit_target_yearly}
              onChange={(e) => setForm({ ...form, profit_target_yearly: e.target.value })}
              data-testid="settings-profit-yearly" />
          </Field>
          <Field label="Gewinnziel / Monat (€)">
            <input type="number" step="0.01" className="field" value={form.profit_target_monthly}
              onChange={(e) => setForm({ ...form, profit_target_monthly: e.target.value })}
              data-testid="settings-profit-monthly" />
          </Field>
        </div>

        <div className="border-t border-[#E5E7EB] pt-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="label-eyebrow">Break-even</div>
              <div className="font-display text-lg mt-1">{formatEUR(form.break_even_monthly)}</div>
            </div>
            <div>
              <div className="label-eyebrow">Umsatzziel</div>
              <div className="font-display text-lg mt-1">{formatEUR(form.revenue_target_monthly)}</div>
            </div>
            <div>
              <div className="label-eyebrow">Gewinnziel</div>
              <div className="font-display text-lg mt-1">{formatEUR(form.profit_target_monthly)}</div>
            </div>
          </div>
        </div>

        {/* Default-Kosten pro Projekt (für Deckungsbeitrag) */}
        <div className="border-t border-[#E5E7EB] pt-4" data-testid="settings-default-costs">
          <div className="label-eyebrow mb-2">Standard-Kosten pro Projekt (Deckungsbeitrag)</div>
          <p className="text-xs text-[#5B6371] mb-4">
            Diese Werte werden auf ALLE Projekte angewendet, die keine eigenen Kostenwerte gesetzt haben — sparen dir das manuelle Ausfüllen pro Kurs. Individuelle Werte im AVGS-/FBW-Formular überschreiben diese Defaults.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Personal €/UE (Dozent)">
              <input type="number" step="0.01" min="0" className="field"
                value={form.default_personnel_cost_per_ue}
                onChange={(e) => setForm({ ...form, default_personnel_cost_per_ue: e.target.value })}
                data-testid="settings-default-personnel"
                placeholder="z.B. 45,00" />
            </Field>
            <Field label="Material pauschal (€) pro Projekt">
              <input type="number" step="0.01" min="0" className="field"
                value={form.default_material_cost_flat}
                onChange={(e) => setForm({ ...form, default_material_cost_flat: e.target.value })}
                data-testid="settings-default-material"
                placeholder="z.B. 50,00" />
            </Field>
            <Field label="Raum pauschal (€) pro Projekt">
              <input type="number" step="0.01" min="0" className="field"
                value={form.default_room_cost_flat}
                onChange={(e) => setForm({ ...form, default_room_cost_flat: e.target.value })}
                data-testid="settings-default-room"
                placeholder="z.B. 100,00" />
            </Field>
          </div>
          <div className="mt-3 text-xs text-[#5B6371] bg-[#F8FAFC] border border-[#E5E7EB] rounded-md p-3">
            <strong>Beispiel:</strong> Ein AVGS-Kurs mit 50 UE bekommt automatisch{" "}
            {parseFloat(form.default_personnel_cost_per_ue || 0) * 50 +
             parseFloat(form.default_material_cost_flat || 0) +
             parseFloat(form.default_room_cost_flat || 0)
              ? formatEUR(parseFloat(form.default_personnel_cost_per_ue || 0) * 50 +
                          parseFloat(form.default_material_cost_flat || 0) +
                          parseFloat(form.default_room_cost_flat || 0))
              : "0,00 €"}
            {" "}an direkten Kosten zugewiesen ({parseFloat(form.default_personnel_cost_per_ue || 0)} × 50 UE Personal + {parseFloat(form.default_material_cost_flat || 0)} Material + {parseFloat(form.default_room_cost_flat || 0)} Raum).
          </div>
        </div>

        {/* Startguthaben für Liquiditätsplanung */}
        <div className="border-t border-[#E5E7EB] pt-4" data-testid="settings-starting-balance">
          <div className="label-eyebrow mb-2">Startguthaben — aktueller Kontostand</div>
          <p className="text-xs text-[#5B6371] mb-3">
            Trage hier dein aktuelles Bankguthaben ein. Der Cashflow-Simulator startet damit und berechnet den Kontostand Monat für Monat (Startguthaben + Einnahmen − Ausgaben).
          </p>
          <Field label="Aktueller Kontostand (€)">
            <input type="number" step="0.01" className="field"
              value={form.starting_balance ?? 0}
              onChange={(e) => setForm({ ...form, starting_balance: e.target.value })}
              data-testid="settings-starting-balance-input"
              placeholder="0,00" />
            <div className="text-[10px] text-[#94A3B8] mt-1">Bei neuem Monat einmal manuell aktualisieren. Später kommt optionaler Bank-Import.</div>
          </Field>
        </div>

        {/* Cashflow-Simulator: Ampel-Schwellen */}
        <div className="border-t border-[#E5E7EB] pt-4" data-testid="settings-cashflow-thresholds">
          <div className="label-eyebrow mb-2">Cashflow-Simulator — Ampel-Schwellen</div>
          <p className="text-xs text-[#5B6371] mb-4">
            Bei welchem Netto-Saldo im gewählten Zeitraum wechselt die Ampel zwischen den Farben? Passe an dein Risiko-Empfinden an.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Grün ab (€)">
              <input type="number" step="100" className="field"
                value={form.cashflow_green_threshold ?? 5000}
                onChange={(e) => setForm({ ...form, cashflow_green_threshold: e.target.value })}
                data-testid="settings-threshold-green"
                placeholder="5000" />
              <div className="text-[10px] text-[#0AA09E] mt-1">Komfortable Lage</div>
            </Field>
            <Field label="Gelb ab (€)">
              <input type="number" step="100" className="field"
                value={form.cashflow_yellow_threshold ?? 0}
                onChange={(e) => setForm({ ...form, cashflow_yellow_threshold: e.target.value })}
                data-testid="settings-threshold-yellow"
                placeholder="0" />
              <div className="text-[10px] text-[#F59E0B] mt-1">Vorsicht</div>
            </Field>
            <Field label="Orange ab (€)">
              <input type="number" step="100" className="field"
                value={form.cashflow_orange_threshold ?? -3000}
                onChange={(e) => setForm({ ...form, cashflow_orange_threshold: e.target.value })}
                data-testid="settings-threshold-orange"
                placeholder="-3000" />
              <div className="text-[10px] text-[#FF8C5C] mt-1">Achtung — eng · Darunter Rot</div>
            </Field>
          </div>
        </div>

        <div className="border-t border-[#E5E7EB] pt-4">
          <div className="label-eyebrow mb-2">AVGS-Maßnahmetypen mit individuellem UE-Preis</div>
          <p className="text-xs text-[#5B6371] mb-3">
            Beim Anlegen einer AVGS-Rechnung kannst du diese Typen wählen — der Endbetrag wird automatisch mit dem jeweiligen UE-Preis berechnet.
            <br />
            <span className="text-[#0AA09E]">Preisänderungen im Zeitverlauf</span>: klicke bei einem Typ auf „+ Preisänderung", um zusätzliche Preise mit Gültigkeitsdatum zu hinterlegen (z.B. „ab 01.08.2026: 61,51 €").
          </p>
          <div className="space-y-3">
            {(form.avgs_measure_types || []).map((t, idx) => (
              <div key={idx} className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E5E7EB]">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <input type="text" className="field col-span-7" placeholder="Name (z.B. Einführung in den Arbeitsmarkt)"
                    value={t.name}
                    onChange={(e) => {
                      const arr = [...(form.avgs_measure_types || [])];
                      arr[idx] = { ...arr[idx], name: e.target.value };
                      setForm({ ...form, avgs_measure_types: arr });
                    }}
                    data-testid={`measure-type-name-${idx}`} />
                  <input type="number" step="0.01" className="field col-span-3" placeholder="€/UE Standard"
                    value={t.price_per_ue}
                    onChange={(e) => {
                      const arr = [...(form.avgs_measure_types || [])];
                      arr[idx] = { ...arr[idx], price_per_ue: e.target.value };
                      setForm({ ...form, avgs_measure_types: arr });
                    }}
                    data-testid={`measure-type-price-${idx}`} />
                  <button type="button"
                    onClick={() => {
                      const arr = (form.avgs_measure_types || []).filter((_, i) => i !== idx);
                      setForm({ ...form, avgs_measure_types: arr });
                    }}
                    className="col-span-2 text-[#FF6B5C] hover:text-[#FF8576] text-xs flex items-center gap-1 justify-center"
                    data-testid={`measure-type-delete-${idx}`}>
                    <Trash size={14} /> Löschen
                  </button>
                </div>
                {/* Preisänderungen im Zeitverlauf */}
                <div className="mt-3 pl-3 border-l-2 border-[#0AA09E] space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Preisänderungen im Zeitverlauf</div>
                  {(t.price_history || []).map((p, pIdx) => (
                    <div key={pIdx} className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" className="field col-span-5" placeholder='Label (z.B. "ab August 2026")'
                        value={p.label || ""}
                        onChange={(e) => {
                          const arr = [...(form.avgs_measure_types || [])];
                          const ph = [...(arr[idx].price_history || [])];
                          ph[pIdx] = { ...ph[pIdx], label: e.target.value };
                          arr[idx] = { ...arr[idx], price_history: ph };
                          setForm({ ...form, avgs_measure_types: arr });
                        }}
                        data-testid={`price-history-label-${idx}-${pIdx}`} />
                      <input type="date" className="field col-span-3"
                        value={p.effective_from || ""}
                        onChange={(e) => {
                          const arr = [...(form.avgs_measure_types || [])];
                          const ph = [...(arr[idx].price_history || [])];
                          ph[pIdx] = { ...ph[pIdx], effective_from: e.target.value || null };
                          arr[idx] = { ...arr[idx], price_history: ph };
                          setForm({ ...form, avgs_measure_types: arr });
                        }}
                        data-testid={`price-history-date-${idx}-${pIdx}`} />
                      <input type="number" step="0.01" className="field col-span-2" placeholder="€/UE"
                        value={p.price_per_ue}
                        onChange={(e) => {
                          const arr = [...(form.avgs_measure_types || [])];
                          const ph = [...(arr[idx].price_history || [])];
                          ph[pIdx] = { ...ph[pIdx], price_per_ue: e.target.value };
                          arr[idx] = { ...arr[idx], price_history: ph };
                          setForm({ ...form, avgs_measure_types: arr });
                        }}
                        data-testid={`price-history-price-${idx}-${pIdx}`} />
                      <button type="button"
                        onClick={() => {
                          const arr = [...(form.avgs_measure_types || [])];
                          arr[idx] = { ...arr[idx], price_history: (arr[idx].price_history || []).filter((_, i) => i !== pIdx) };
                          setForm({ ...form, avgs_measure_types: arr });
                        }}
                        className="col-span-2 text-[#FF6B5C] hover:text-[#FF8576] text-xs flex items-center gap-1 justify-center"
                        data-testid={`price-history-delete-${idx}-${pIdx}`}>
                        <Trash size={12} /> Entfernen
                      </button>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => {
                      const arr = [...(form.avgs_measure_types || [])];
                      arr[idx] = { ...arr[idx], price_history: [...(arr[idx].price_history || []), { label: "", effective_from: "", price_per_ue: t.price_per_ue || 0 }] };
                      setForm({ ...form, avgs_measure_types: arr });
                    }}
                    className="inline-flex items-center gap-1 text-xs text-[#0AA09E] hover:underline"
                    data-testid={`price-history-add-${idx}`}>
                    <Plus size={12} /> Preisänderung
                  </button>
                </div>
              </div>
            ))}
            <button type="button"
              onClick={() => setForm({
                ...form,
                avgs_measure_types: [...(form.avgs_measure_types || []), { name: "", price_per_ue: 0, price_history: [] }],
              })}
              className="inline-flex items-center gap-1.5 text-sm text-[#0AA09E] hover:underline mt-2"
              data-testid="measure-type-add">
              <Plus size={14} /> Maßnahme-Typ hinzufügen
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving}
            className="px-5 py-2.5 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md disabled:opacity-60"
            data-testid="settings-save">
            {saving ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </form>

      <PaymentAuditSection />

      <UsersSection />

      <DocsSection />
    </div>
  );
}

function DocsSection() {
  const downloadFile = async (endpoint, filename) => {
    try {
      const resp = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };
  return (
    <section className="space-y-3" data-testid="docs-section">
      <div>
        <div className="label-eyebrow">Dokumente</div>
        <h2 className="font-display text-2xl font-bold mt-1">Unsere Geschichte &amp; Datenschutz</h2>
        <p className="text-sm text-[#5B6371] mt-1">Offizielle Dokumente zum Herunterladen — für Auditoren, Prüfer oder eigene Referenz.</p>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-base font-semibold text-[#1F242E]">We finance — Vom Liquiditäts-Risiko zur planbaren Zukunft</div>
          <div className="text-xs text-[#5B6371] mt-1">PDF · Entwicklungsgeschichte, Funktionsumfang &amp; DSGVO-Konformität</div>
          <div className="text-xs text-[#94A3B8] mt-0.5">Stand: Juni 2026 · DSGVO Art. 5, 25, 30, 32 · Privacy by Design</div>
        </div>
        <button
          onClick={() => downloadFile("/docs/azav-nachweis", "We_finance_Reise_zur_Liquiditaetssicherheit.pdf")}
          data-testid="download-azav-pdf"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2.5"
        >
          PDF herunterladen
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-base font-semibold text-[#1F242E]">Liquiditätsnachweis Mai – Juli 2026</div>
          <div className="text-xs text-[#5B6371] mt-1">PDF · Monatsübersicht mit Einnahmen, Ausgaben und Netto-Saldo</div>
          <div className="text-xs text-[#94A3B8] mt-0.5">Netto-Überschuss +61.688,89 € · zur Vorlage beim AZAV-Prüfer</div>
        </div>
        <button
          onClick={() => downloadFile("/docs/liquiditaet-mai-juli", "We_finance_Liquiditaetsnachweis_Mai_Juli_2026.pdf")}
          data-testid="download-liquiditaet-mai-juli"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2.5"
        >
          PDF herunterladen
        </button>
      </div>
    </section>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwModal, setPwModal] = useState(null); // user object or null
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (e) {
      if (e.response?.status === 403) setUsers(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onSetPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    try {
      await api.post(`/auth/users/${pwModal.id}/set-password`, { new_password: newPw });
      toast.success(`Passwort für ${pwModal.email} zurückgesetzt`);
      setPwModal(null);
      setNewPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  if (users === null) return null; // not admin → hide

  return (
    <section className="space-y-3" data-testid="users-section">
      <div>
        <div className="label-eyebrow flex items-center gap-1.5"><Users size={14} /> Benutzerverwaltung</div>
        <h2 className="font-display text-2xl font-bold mt-1">Mitarbeiter & Passwörter</h2>
        <p className="text-sm text-[#5B6371] mt-1">Du kannst hier das Passwort jedes Mitarbeiters zurücksetzen.</p>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
              <th className="text-left px-4 py-3 font-medium">E-Mail</th>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Rolle</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[#94A3B8]">Lade …</td></tr>)}
            {!loading && users.map((u) => (
              <tr key={u.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.email}</td>
                <td className="px-4 py-3 text-[#5B6371]">{u.name || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                    u.role === "admin" ? "bg-[#0AA09E] text-white" : "bg-[#F1F5F9] text-[#5B6371]"
                  }`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setPwModal(u); setNewPw(""); }}
                    className="inline-flex items-center gap-1.5 text-xs text-[#0AA09E] hover:underline px-2 py-1"
                    data-testid={`user-reset-${u.id}`}>
                    <Key size={14} /> Passwort zurücksetzen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pwModal && (
        <Modal title={`Passwort zurücksetzen — ${pwModal.email}`} onClose={() => setPwModal(null)}>
          <form onSubmit={onSetPassword} className="space-y-4" data-testid="set-password-form">
            <Field label="Neues Passwort">
              <input type="text" required minLength={6} className="field font-mono" value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="mindestens 6 Zeichen"
                data-testid="new-password-input" autoFocus />
              <p className="text-xs text-[#5B6371] mt-1">
                Teile dem Mitarbeiter das neue Passwort sicher mit. Er kann sich danach normal einloggen.
              </p>
            </Field>
            <div className="bg-[#FFF1EE] border border-[#FFD9D2] rounded-md px-3 py-2 text-xs text-[#5B6371]">
              ⓘ Vorhandene Account-Sperren (nach 5 Fehlversuchen) werden automatisch aufgehoben.
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setPwModal(null)} className="px-4 py-2 text-sm text-[#5B6371]" data-testid="cancel-set-password">
                Abbrechen
              </button>
              <button type="submit" className="px-5 py-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md" data-testid="submit-set-password">
                Passwort setzen
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
